import { groupBy } from 'lodash';

import {
  getConversionFactorValue,
  sumConversionFactors,
} from './conversion-factors.helpers';
import { filterListElementsContributingToVersionTotals } from './element-list-group-contributors.helpers';
import { getActiveRootElementIdsInProposal } from './proposal.helpers';
import { isElement } from './recursive-element.helpers';
import type { Baseline, OneOfListElements } from '../models/project.model';
import type { IProposal } from '../models/proposals.model';
import type { Results, ResultsRecord } from '../models/unit.model';

export type ScenarioGroupAggregateRow = {
  groupKey: string;
  resultsSum: Results;
};

export type BuildSbefRootGroupAggregatesParams = {
  version: Baseline;
  baselineRecord: ResultsRecord;
  proposalRecordsByProposalId: Record<string, ResultsRecord>;
  proposals: readonly IProposal[];
  getGroupKey: (element: OneOfListElements) => string;
};

/**
 * Root-level SBEF (or other) groups with summed {@link Results} per scenario id
 * (baseline = {@link Baseline.id}, proposals = {@link IProposal.id}).
 * Baseline uses contributing roots only; each proposal uses the same grouping but
 * sums only elements active in that proposal — aligned with {@link useGroupSectionProps}.
 */
export const buildSbefRootGroupAggregatesByScenarioId = ({
  version,
  baselineRecord,
  proposalRecordsByProposalId,
  proposals,
  getGroupKey,
}: BuildSbefRootGroupAggregatesParams): Record<
  string,
  readonly ScenarioGroupAggregateRow[]
> => {
  const roots = version.elements.filter(isElement);
  if (!roots.length) return {};

  const groups = groupBy(roots, getGroupKey);
  const activeIdsByProposal: Record<string, Set<string>> = {};
  for (const p of proposals) {
    activeIdsByProposal[p.id] = new Set(
      getActiveRootElementIdsInProposal(version, p, false),
    );
  }

  const out: Record<string, ScenarioGroupAggregateRow[]> = {};

  const baselineRows: ScenarioGroupAggregateRow[] = [];
  for (const [groupKey, items] of Object.entries(groups)) {
    const contributing = filterListElementsContributingToVersionTotals(
      items,
      version,
    );
    baselineRows.push({
      groupKey,
      resultsSum: sumConversionFactors(
        ...contributing.map((el) => baselineRecord[el.id] ?? {}),
      ),
    });
  }
  out[version.id] = baselineRows;

  for (const proposal of proposals) {
    const record = proposalRecordsByProposalId[proposal.id];
    if (!record) continue;
    const activeSet = activeIdsByProposal[proposal.id];
    const rows: ScenarioGroupAggregateRow[] = [];
    for (const [groupKey, items] of Object.entries(groups)) {
      rows.push({
        groupKey,
        resultsSum: sumConversionFactors(
          ...items
            .filter((el) => activeSet?.has(el.id))
            .map((el) => record[el.id] ?? {}),
        ),
      });
    }
    out[proposal.id] = rows;
  }

  return out;
};

export type ComputeMaxGroupSectionBarScaleParams =
  BuildSbefRootGroupAggregatesParams & {
    getResultsPerGFA: (results: Results) => Results;
  };

/**
 * Maximum kg CO₂e/m² and SEK/m² after {@link getResultsPerGFA} over every root-level
 * group total, across baseline and each proposal (same sums as group section rows).
 */
export const computeMaxGroupSectionBarScalePerGfa = ({
  getResultsPerGFA,
  ...buildParams
}: ComputeMaxGroupSectionBarScaleParams): {
  co2eMax: number;
  sekMax: number;
} => {
  const byScenario = buildSbefRootGroupAggregatesByScenarioId(buildParams);
  return maxCo2SekPerGfaFromScenarioGroupRows(byScenario, getResultsPerGFA);
};

/**
 * Peak CO₂e and SEK **per GFA** after {@link getResultsPerGFA} over all
 * {@link ScenarioGroupAggregateRow} rows in every scenario bucket.
 */
export const maxCo2SekPerGfaFromScenarioGroupRows = (
  byScenarioId: Record<string, readonly ScenarioGroupAggregateRow[]>,
  getResultsPerGFA: (results: Results) => Results,
): { co2eMax: number; sekMax: number } => {
  let maxCo2 = 0;
  let maxSek = 0;

  for (const rows of Object.values(byScenarioId)) {
    for (const row of rows) {
      const perGfa = getResultsPerGFA(row.resultsSum);
      const co2 = getConversionFactorValue(perGfa, 'co2e') ?? 0;
      const sek = getConversionFactorValue(perGfa, 'sek') ?? 0;
      if (co2 > maxCo2) maxCo2 = co2;
      if (sek > maxSek) maxSek = sek;
    }
  }

  return { co2eMax: maxCo2, sekMax: maxSek };
};
